/**
 * @fileoverview Text Editor Interface Component
 * 
 * The main textarea interface for text editing with enhanced functionality
 */

import React, { forwardRef, useCallback } from 'react';

/**
 * Text Editor Interface Component
 * 
 * @param {Object} props - Component props
 * @param {string} props.content - Current content
 * @param {function} props.onChange - Content change handler
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Whether editor is disabled
 * @param {number} props.maxLength - Maximum character length
 * @param {string} props.testId - Test ID
 */
const TextEditorInterface = forwardRef(({ 
  content, 
  onChange, 
  placeholder, 
  disabled, 
  maxLength,
  testId 
}, ref) => {
  
  /**
   * Handle textarea change
   */
  const handleChange = useCallback((event) => {
    const newContent = event.target.value;
    
    // Enforce max length
    if (newContent.length <= maxLength) {
      onChange(newContent);
    }
  }, [onChange, maxLength]);

  /**
   * Handle key down for tab insertion
   */
  const handleKeyDown = useCallback((event) => {
    // Handle tab insertion for better code editing
    if (event.key === 'Tab') {
      event.preventDefault();
      
      const textarea = event.target;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      // Insert tab character
      const newContent = 
        content.substring(0, start) + 
        '\t' + 
        content.substring(end);
      
      onChange(newContent);
      
      // Set cursor position after tab
      setTimeout(() => {
        textarea.setSelectionRange(start + 1, start + 1);
      }, 0);
    }
  }, [content, onChange]);

  return (
    <div className="text-editor-interface">
      <textarea
        ref={ref}
        value={content}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        className="text-editor-textarea"
        data-testid={testId}
        rows={20}
        cols={80}
        spellCheck={true}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        wrap="soft"
      />
    </div>
  );
});

TextEditorInterface.displayName = 'TextEditorInterface';

export { TextEditorInterface };